import { Form, Input, Modal, Select, Tag } from 'antd'
import { useContext, useEffect } from 'react'
import { AuthenticationContext } from '../../../../AuthenticationContext'
import {
  getPromptAncestryLabel,
  GoldenPrompt,
  GoldenPromptCreateInput,
  GoldenPromptUpdateInput,
  PromptType,
} from './goldenPromptsAdminModel'
import {
  useCreateGoldenPromptAdminMutation,
  useUpdateGoldenPromptAdminMutation,
} from './goldenPromptsAdminQueries'

type FormValues = {
  prompt_text: string
  prompt_type: PromptType
  user_id?: string
  session_ref?: string
  ancestor_id?: string
}

type Props = {
  open: boolean
  editing: GoldenPrompt | null
  onClose: () => void
}

const GoldenPromptFormModal = ({ open, editing, onClose }: Props) => {
  const [form] = Form.useForm<FormValues>()
  const { currentUser } = useContext(AuthenticationContext)
  const createMutation = useCreateGoldenPromptAdminMutation()
  const updateMutation = useUpdateGoldenPromptAdminMutation()

  useEffect(() => {
    if (!open) {
      return
    }
    if (editing) {
      form.setFieldsValue({
        prompt_text: editing.prompt_text,
        prompt_type: editing.prompt_type,
        user_id: editing.user_id,
        session_ref: editing.session_ref ?? undefined,
        ancestor_id: editing.ancestor_id ?? undefined,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        prompt_type: 'normal',
        user_id: currentUser?.userId,
      })
    }
  }, [open, editing, form, currentUser])

  const handleOk = async () => {
    const values = await form.validateFields()
    if (editing) {
      const input: GoldenPromptUpdateInput = {
        prompt_text: values.prompt_text,
        prompt_type: values.prompt_type,
      }
      updateMutation.mutate(
        { promptId: editing.prompt_id, input },
        { onSuccess: onClose },
      )
    } else {
      const input: GoldenPromptCreateInput = {
        user_id: values.user_id ?? '',
        prompt_text: values.prompt_text,
        prompt_type: values.prompt_type,
        session_ref: values.session_ref || null,
        ancestor_id: values.ancestor_id || null,
      }
      createMutation.mutate(input, { onSuccess: onClose })
    }
  }

  const submitting = createMutation.isLoading || updateMutation.isLoading
  const ancestryLabel = editing ? getPromptAncestryLabel(editing.ancestor_id) : null

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={editing ? 'Save' : 'Create'}
      confirmLoading={submitting}
      title={editing ? 'Edit Golden Prompt' : 'New Golden Prompt'}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {ancestryLabel && (
          <div style={{ marginBottom: 16 }}>
            <Tag color={ancestryLabel === 'original' ? 'blue' : 'orange'}>
              {ancestryLabel === 'original' ? 'Original' : 'Mutant'}
            </Tag>
          </div>
        )}
        <Form.Item
          name="prompt_text"
          label="Prompt text"
          rules={[{ required: true, whitespace: true, message: 'Prompt text is required' }]}
        >
          <Input.TextArea rows={6} autoSize={{ minRows: 4, maxRows: 12 }} />
        </Form.Item>
        <Form.Item
          name="prompt_type"
          label="Prompt type"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { value: 'normal', label: 'Normal' },
              { value: 'adversarial', label: 'Adversarial' },
            ]}
          />
        </Form.Item>
        {!editing && (
          <>
            <Form.Item
              name="user_id"
              label="Author (user ID)"
              rules={[{ required: true, whitespace: true, message: 'User ID is required' }]}
            >
              <Input placeholder="UUID of the prompt author" />
            </Form.Item>
            <Form.Item name="session_ref" label="Source session (optional)">
              <Input placeholder="Chat session UUID, if any" />
            </Form.Item>
            <Form.Item name="ancestor_id" label="Ancestor prompt (optional)">
              <Input placeholder="Parent golden prompt UUID, if any" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}

export default GoldenPromptFormModal
